'use client';

import { PontCore } from '@/services/pontCore';
import { api } from '@/utils/api';
import { responseInterceptor } from '@/utils/responseInterceptor';
import { interceptorWithToken } from '@verifime/api-definition';
import { usePasswordless, useTokens } from '@verifime/cognito-passwordless-auth';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';

const NO_NEED_AUTH_CHECK_PATH = ['/sign-up', '/recover-invitation', '/token'];

export default function AuthenticatedContainer({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const { tokensParsed } = usePasswordless();
  const { getTokens, getTokensParsed } = useTokens();
  const router = useRouter();

  React.useEffect(() => {
    const validateTokensParsed = (tokensParsedToValidate: typeof tokensParsed) =>
      tokensParsedToValidate && new Date() < tokensParsedToValidate.expireAt;

    if (!validateTokensParsed(tokensParsed)) {
      getTokensParsed().then((newTokensParsed) => {
        if (!validateTokensParsed(newTokensParsed)) {
          const currentPath = router.pathname;
          // The home (login) page always no need auth, keep it as it is
          if ('/' === currentPath) {
            return;
          }

          if (!NO_NEED_AUTH_CHECK_PATH.find((path) => currentPath.startsWith(path))) {
            router.push({
              pathname: '/',
              query: { from: encodeURIComponent(router.asPath) },
            });
          }
        }
      });
    }
  }, [getTokensParsed, router, tokensParsed]);

  PontCore.useFetch(async (url, options) => {
    const { accessToken: token } = (await getTokens()) || {};
    return await responseInterceptor(url, token, {
      ...options,
      tokenRequired: !NO_NEED_AUTH_CHECK_PATH.includes(router.pathname),
    });
  });

  useEffect(() => {
    api.use(
      interceptorWithToken({
        getToken: async () => getTokens().then((data) => data?.accessToken),
      }),
    );
  }, [getTokens]);

  return <>{children} </>;
}
